import React, { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import { Plus, Building, Edit, Trash2, Star, CreditCard, Percent } from 'lucide-react';
import ConfigurarTaxasConta from '@/pages/ConfigurarTaxasConta';

export default function ContasBancarias() {
  const [contas, setContas] = useState([]);
  const [bancosDisponiveis, setBancosDisponiveis] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingConta, setEditingConta] = useState(null);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [deletingConta, setDeletingConta] = useState(null);
  
  const [form, setForm] = useState({
    nome: '',
    banco_codigo: '',
    banco_nome: '',
    agencia: '',
    conta: '',
    tipo_conta: 'corrente',
    titular: '',
    documento: '',
    ativo: true,
    padrao: false
  });

  useEffect(() => {
    loadContas();
  }, []);

  const loadContas = async () => {
    try {
      const res = await api.get('/contas-bancarias');
      setContas(res.data.contas || []);
      setBancosDisponiveis(res.data.bancos_disponiveis || []);
    } catch (error) {
      toast.error('Erro ao carregar contas bancárias');
    } finally {
      setLoading(false);
    }
  };

  const handleBancoChange = (codigo) => {
    const banco = bancosDisponiveis.find(b => b.codigo === codigo);
    setForm({
      ...form,
      banco_codigo: codigo,
      banco_nome: banco?.nome || ''
    });
  };

  const openCreateDialog = () => {
    setEditingConta(null);
    setForm({
      nome: '',
      banco_codigo: '',
      banco_nome: '',
      agencia: '',
      conta: '',
      tipo_conta: 'corrente',
      titular: '',
      documento: '',
      ativo: true,
      padrao: false
    });
    setDialogOpen(true);
  };

  const openEditDialog = (conta) => {
    setEditingConta(conta);
    setForm({
      nome: conta.nome || '',
      banco_codigo: conta.banco_codigo || '',
      banco_nome: conta.banco_nome || '',
      agencia: conta.agencia || '',
      conta: conta.conta || '',
      tipo_conta: conta.tipo_conta || 'corrente',
      titular: conta.titular || '',
      documento: conta.documento || '',
      ativo: conta.ativo !== false,
      padrao: conta.padrao === true
    });
    setDialogOpen(true);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.nome || !form.banco_codigo || !form.agencia || !form.conta) {
      toast.error('Preencha os campos obrigatórios');
      return;
    }
    
    try {
      if (editingConta) {
        await api.put(`/contas-bancarias/${editingConta.id}`, form);
        toast.success('Conta atualizada com sucesso!');
      } else {
        await api.post('/contas-bancarias', form);
        toast.success('Conta criada com sucesso!');
      }
      setDialogOpen(false);
      loadContas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao salvar conta');
    }
  };

  const handleDelete = async () => {
    try {
      await api.delete(`/contas-bancarias/${deletingConta.id}`);
      toast.success('Conta excluída com sucesso!');
      setDeleteDialogOpen(false);
      loadContas();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erro ao excluir conta');
    }
  };

  const handleSetPadrao = async (conta) => {
    try {
      await api.put(`/contas-bancarias/${conta.id}`, { padrao: true });
      toast.success('Conta definida como padrão!');
      loadContas();
    } catch (error) {
      toast.error('Erro ao definir conta padrão');
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2">
            <Building className="w-6 h-6" />
            Contas Bancárias
          </h1>
          <p className="text-muted-foreground">Fonte oficial de recebimento · configure as formas aceitas e as taxas em <Percent className="w-3 h-3 inline" /></p>
        </div>
        <Button onClick={openCreateDialog}>
          <Plus className="w-4 h-4 mr-2" />
          Nova Conta
        </Button>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Banco</TableHead>
                <TableHead>Agência</TableHead>
                <TableHead>Conta</TableHead>
                <TableHead>Tipo</TableHead>
                <TableHead>Titular</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Ações</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8">Carregando...</TableCell>
                </TableRow>
              ) : contas.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                    <Building className="w-12 h-12 mx-auto mb-2 opacity-30" />
                    <p>Nenhuma conta bancária cadastrada</p>
                    <p className="text-sm">Clique em "Nova Conta" para adicionar</p>
                  </TableCell>
                </TableRow>
              ) : (
                contas.map((conta) => (
                  <TableRow key={conta.id}>
                    <TableCell className="font-medium">
                      <div className="flex items-center gap-2">
                        {conta.nome}
                        {conta.padrao && (
                          <Star className="w-4 h-4 text-yellow-500 fill-yellow-500" />
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <CreditCard className="w-4 h-4 text-muted-foreground" />
                        {conta.banco_nome}
                      </div>
                    </TableCell>
                    <TableCell>{conta.agencia}</TableCell>
                    <TableCell>{conta.conta}</TableCell>
                    <TableCell className="capitalize">{conta.tipo_conta}</TableCell>
                    <TableCell>{conta.titular}</TableCell>
                    <TableCell>
                      <Badge variant={conta.ativo ? 'default' : 'secondary'}>
                        {conta.ativo ? 'Ativa' : 'Inativa'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-1">
                        <ConfigurarTaxasConta conta={conta} />
                        {!conta.padrao && (
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleSetPadrao(conta)}
                            title="Definir como padrão"
                          >
                            <Star className="w-4 h-4" />
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={() => openEditDialog(conta)}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-600"
                          onClick={() => { setDeletingConta(conta); setDeleteDialogOpen(true); }}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>

      {/* Dialog de Criar/Editar */}
      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>
              {editingConta ? 'Editar Conta Bancária' : 'Nova Conta Bancária'}
            </DialogTitle>
          </DialogHeader>
          
          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label>Nome da Conta *</Label>
              <Input
                value={form.nome}
                onChange={(e) => setForm({...form, nome: e.target.value})}
                placeholder="Ex: Conta Principal, Conta Reserva..."
                required
              />
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Banco *</Label>
                <Select value={form.banco_codigo} onValueChange={handleBancoChange}>
                  <SelectTrigger>
                    <SelectValue placeholder="Selecione o banco" />
                  </SelectTrigger>
                  <SelectContent>
                    {bancosDisponiveis.map(banco => (
                      <SelectItem key={banco.codigo} value={banco.codigo}>
                        {banco.codigo} - {banco.nome}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="space-y-2">
                <Label>Tipo de Conta</Label>
                <Select value={form.tipo_conta} onValueChange={(v) => setForm({...form, tipo_conta: v})}>
                  <SelectTrigger>
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="corrente">Corrente</SelectItem>
                    <SelectItem value="poupanca">Poupança</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Agência *</Label>
                <Input
                  value={form.agencia}
                  onChange={(e) => setForm({...form, agencia: e.target.value})}
                  placeholder="0000"
                  required
                />
              </div>
              <div className="space-y-2">
                <Label>Conta *</Label>
                <Input
                  value={form.conta}
                  onChange={(e) => setForm({...form, conta: e.target.value})}
                  placeholder="00000-0"
                  required
                />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Titular</Label>
              <Input
                value={form.titular}
                onChange={(e) => setForm({...form, titular: e.target.value})}
                placeholder="Nome do titular da conta"
              />
            </div>

            <div className="space-y-2">
              <Label>CPF/CNPJ</Label>
              <Input
                value={form.documento}
                onChange={(e) => setForm({...form, documento: e.target.value})}
                placeholder="00.000.000/0000-00"
              />
            </div>

            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.ativo}
                  onCheckedChange={(v) => setForm({...form, ativo: v})}
                />
                <Label>Conta ativa</Label>
              </div>
              <div className="flex items-center gap-2">
                <Switch
                  checked={form.padrao}
                  onCheckedChange={(v) => setForm({...form, padrao: v})}
                />
                <Label>Conta padrão</Label>
              </div>
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>
                Cancelar
              </Button>
              <Button type="submit">
                {editingConta ? 'Salvar Alterações' : 'Criar Conta'}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      {/* Dialog de Confirmação de Exclusão */}
      <Dialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="text-red-600">Excluir Conta Bancária</DialogTitle>
          </DialogHeader>
          <p>Tem certeza que deseja excluir a conta <strong>{deletingConta?.nome}</strong>?</p>
          <p className="text-sm text-muted-foreground">Esta ação não pode ser desfeita.</p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDeleteDialogOpen(false)}>
              Cancelar
            </Button>
            <Button variant="destructive" onClick={handleDelete}>
              Excluir
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
